package ai.jessie.web.documents

import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem
import ai.jessie.web.onboarding.unavailableProblem

internal fun DocumentRequestFailure.tenantFailure() = OnboardingRequestFailure(status, detail.code)

// UC-006 tenant outcomes (401, 403, 404, ORGANISATION_REQUIRED): no tenant data may stay.
internal fun DocumentRequestFailure.deniesAccess() = tenantFailure().deniesAccess()

// Loading the inbox: the unchanged UC-006 presentation.
internal fun DocumentRequestFailure.loadProblem(): OnboardingProblem =
    if (status == 0) unavailableProblem() else tenantFailure().problem()

// B8: one message per alternative flow, without internal detail. A hidden duplicate (A8) is never
// reported, so it has no message here.
internal fun DocumentRequestFailure.uploadProblem(confirming: Boolean): DocumentProblem =
    rejection()
        ?: when {
            // A4: role or active organisation no longer confirmed at upload.
            confirming && (status == HTTP_FORBIDDEN || status == HTTP_NOT_FOUND) ->
                DocumentProblem(
                    "Der Upload kann nicht fortgesetzt werden, weil dein Zugriff auf diese " +
                        "Organisation nicht mehr bestätigt ist. Das Dokument wurde nicht hinzugefügt."
                )
            // A6, or an unanswered transfer: nothing may be presented as stored.
            confirming && (status == 0 || status >= HTTP_SERVER_ERROR) -> notUploadedProblem()
            status == 0 || status >= HTTP_SERVER_ERROR ->
                DocumentProblem(
                    "Die Datei konnte nicht geprüft werden. Es wurde nichts gespeichert. Bitte " +
                        "versuche es erneut."
                )
            else -> DocumentProblem(tenantFailure().problem().message)
        }

// A1-A3 and A7 by the server's code; the proxy's own 413 carries no code.
private fun DocumentRequestFailure.rejection(): DocumentProblem? =
    when (detail.code) {
        "FILE_EMPTY",
        "FILE_TOO_LARGE" -> sizeProblem()
        "CONTENT_UNSUPPORTED" -> contentProblem()
        "DUPLICATE_DOCUMENT" ->
            DocumentProblem(
                "Dieses Dokument ist bereits vorhanden. Es wurde kein zweites Dokument angelegt.",
                detail.existingDocumentId?.takeIf(::validDocumentId),
            )
        "MALWARE_DETECTED" ->
            DocumentProblem(
                "Die Datei wurde aus Sicherheitsgründen abgelehnt und nicht gespeichert. Bitte " +
                    "wähle eine andere Datei."
            )
        "INVALID_REQUEST" ->
            DocumentProblem(
                "Die Datei konnte nicht gelesen werden. Bitte wähle genau eine Datei mit einem " +
                    "Dateinamen von höchstens 255 Zeichen."
            )
        else -> if (status == HTTP_TOO_LARGE) sizeProblem() else null
    }

// A1, also checked in the browser before any transfer; the server checks again.
internal fun sizeProblem() =
    DocumentProblem(
        "Die Datei muss mindestens 1 Byte und darf höchstens 25 MiB (26’214’400 Bytes) gross " +
            "sein. Sie wurde nicht hinzugefügt. Bitte wähle eine andere Datei."
    )

internal fun contentProblem() =
    DocumentProblem(
        "Die Datei wird nicht unterstützt: Ihr Inhalt muss dem Dateityp entsprechen und darf " +
            "nicht verschlüsselt oder passwortgeschützt sein. Erlaubt sind $SUPPORTED_TYPES. Sie " +
            "wurde nicht hinzugefügt."
    )

internal fun notUploadedProblem() =
    DocumentProblem(
        "Das Dokument wurde nicht hochgeladen. Es wurde nichts gespeichert. Bitte versuche es " +
            "erneut."
    )

// B6 detail: the same neutral absence for a foreign, hidden, unknown or malformed ID.
internal fun unavailableDocumentProblem() =
    OnboardingProblem("Das Dokument ist nicht verfügbar oder du hast keinen Zugriff.")

private const val HTTP_FORBIDDEN = 403
private const val HTTP_NOT_FOUND = 404
private const val HTTP_TOO_LARGE = 413
private const val HTTP_SERVER_ERROR = 500
